import { useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";

const CouponsTab = () => {
  const [coupons, setCoupons] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ code: "", discount_pct: "", expires_at: "", active: true });

  const load = () => api.get("/coupons").then((r) => setCoupons(r.data)).catch(() => toast.error("Could not load coupons"));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.code.trim() || !form.discount_pct) return toast.error("Code and discount are required");
    try {
      await api.post("/coupons", {
        code: form.code.trim(),
        discount_pct: Number(form.discount_pct),
        expires_at: form.expires_at ? new Date(`${form.expires_at}T23:59:59Z`).toISOString() : null,
        active: form.active,
      });
      toast.success(`Coupon ${form.code.toUpperCase()} created`);
      setOpen(false);
      setForm({ code: "", discount_pct: "", expires_at: "", active: true });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create coupon");
    }
  };

  const toggle = async (c, active) => {
    try {
      await api.put(`/coupons/${c.code}`, { code: c.code, discount_pct: c.discount_pct, expires_at: c.expires_at, active });
      setCoupons((prev) => prev.map((x) => (x.code === c.code ? { ...x, active } : x)));
    } catch {
      toast.error("Update failed");
    }
  };

  const remove = async (code) => {
    try {
      await api.delete(`/coupons/${code}`);
      toast.success(`${code} deleted`);
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  if (!coupons) return <div className="h-64 animate-pulse rounded-2xl bg-sand" />;

  return (
    <div data-testid="admin-coupons-tab">
      <div className="mb-5 flex items-center justify-between">
        <p className="text-sm text-clay/60">{coupons.length} coupon codes</p>
        <button
          data-testid="admin-create-coupon-button"
          onClick={() => setOpen(true)}
          className="flex items-center gap-2 rounded-full bg-terra px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-terra-dark active:scale-[0.98]"
        >
          <Plus className="h-4 w-4" /> New coupon
        </button>
      </div>

      <div className="overflow-x-auto rounded-2xl border border-linen bg-white">
        <table className="w-full min-w-[640px] text-left text-sm">
          <thead>
            <tr className="border-b border-linen font-mono text-[10px] uppercase tracking-[0.18em] text-clay/60">
              <th className="px-5 py-3.5">Code</th>
              <th className="px-5 py-3.5">Discount</th>
              <th className="px-5 py-3.5">Expires</th>
              <th className="px-5 py-3.5">Used</th>
              <th className="px-5 py-3.5">Active</th>
              <th className="px-5 py-3.5 text-right">Delete</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-linen/70">
            {coupons.map((c) => (
              <tr key={c.code} className="transition-colors hover:bg-sand/40">
                <td className="px-5 py-3.5 font-mono font-semibold text-espresso">{c.code}</td>
                <td className="px-5 py-3.5">{c.discount_pct}% off</td>
                <td className="px-5 py-3.5 text-clay/70">{c.expires_at ? new Date(c.expires_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "Never"}</td>
                <td className="px-5 py-3.5 font-mono text-clay/70">{c.usage_count}×</td>
                <td className="px-5 py-3.5"><Switch checked={c.active} onCheckedChange={(v) => toggle(c, v)} data-testid={`coupon-active-switch-${c.code}`} /></td>
                <td className="px-5 py-3.5 text-right">
                  <button onClick={() => remove(c.code)} className="rounded-lg p-2 text-clay/60 transition-colors hover:bg-destructive/10 hover:text-destructive" aria-label={`Delete ${c.code}`}>
                    <Trash2 className="h-4 w-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-cream sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-espresso">New coupon</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="mb-1.5 block text-xs font-semibold text-clay">Code</label>
              <input
                data-testid="coupon-form-code"
                value={form.code}
                onChange={(e) => setForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))}
                placeholder="EARTH25"
                className="h-11 w-full rounded-lg border border-linen bg-white px-3.5 font-mono text-sm uppercase tracking-widest outline-none focus:border-terra"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="mb-1.5 block text-xs font-semibold text-clay">Discount %</label>
                <input data-testid="coupon-form-pct" type="number" min="1" max="90" value={form.discount_pct} onChange={(e) => setForm((f) => ({ ...f, discount_pct: e.target.value }))} placeholder="15" className="h-11 w-full rounded-lg border border-linen bg-white px-3.5 text-sm outline-none focus:border-terra" />
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-semibold text-clay">Expiry (optional)</label>
                <input type="date" value={form.expires_at} onChange={(e) => setForm((f) => ({ ...f, expires_at: e.target.value }))} className="h-11 w-full rounded-lg border border-linen bg-white px-3.5 text-sm outline-none focus:border-terra" />
              </div>
            </div>
            <div className="flex items-center justify-between rounded-lg border border-linen bg-white px-3.5 py-2.5">
              <label className="text-xs font-semibold text-clay">Active immediately</label>
              <Switch checked={form.active} onCheckedChange={(v) => setForm((f) => ({ ...f, active: v }))} />
            </div>
            <button onClick={create} data-testid="coupon-form-save" className="w-full rounded-full bg-terra py-3 text-sm font-semibold text-white transition-colors hover:bg-terra-dark">
              Create coupon
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CouponsTab;
